import {createModel} from '@rematch/core';

import {
  getProfileByContacts,
  checkUniqueness,
  ProfilesResponse,
  TProfile,
  TPromotionImage,
  ProfileType,
} from '~/services/promotions';
import {PromotionCategories, TLocationsTree} from '~/services/hot-api';
import {
  TSuggestionCity,
  fetchCitySuggestions,
  fetchLocationsList,
} from '~/services/hot-api/locatinos';
import {
  fetchPromotionCategories,
  FetchPromotionCategoriesParams,
} from '~/services/hot-api/categories';
import {
  FetchBidsParams,
  TLocationPrice,
  checkUrl,
  createCommonPromotion,
  createPromotion,
  fetchLoactionPrices,
  fetchPromotionById,
  fetchRecommendedBids,
} from '~/services/hot-api/promotions';
import {PromotionTextTypes, UserRoles} from '~/utils/constants';
import {isMobileDevice} from '~/utils/browserDetect';
import {
  TFlatCategories,
  TFlatLocationsMap,
  flatCategoires,
  flatLocations,
} from '~/utils/promotions';
import {getUrlInfo, UrlInfoResponse} from '~/services/hotsearch/promotions';

import type {RootModel} from '.';

export type PromotionsState = {
  // Common
  isRequestError: boolean;
  loading: boolean;
  urlStep: any;
  profilesResponse: ProfilesResponse | null;
  profile: TProfile | null;
  thumbnails: TPromotionImage[];
  locationPrice: TLocationPrice | null;
  type: null | 'phone' | 'onlyfans';

  // Categories
  categories: PromotionCategories[];
  flatCategories: TFlatCategories;
  selectedCategories: number[];
  entityType: ProfileType | null;

  // Locations
  locations: TLocationsTree;
  flatLocations: TFlatLocationsMap;
  formatedLocations: any;
  selectedLocations: number[];
  selectedCounties: number[];
  citySuggestions: {
    loading: boolean;
    cities: TSuggestionCity[];
  };

  // Bids
  bids: any;
  bidsRecommended: number | null;

  // Text Ad data
  text: any;
  checkUrlResult: any;
  dailyBudgedModalHidden: boolean;

  // Image Promotion Payload
  image: {
    autoRenew: boolean;
    images: TPromotionImage[];
    url: string;
    mainForm: {
      url: string;
      displayUrl: string;
      headline: string;
      description: string;
    };
  };

  // Url Promo Payload
  url: any;
  promotion: any;
};

const defaultState: PromotionsState = {
  isRequestError: false,
  loading: false,
  type: null,
  profilesResponse: null,
  profile: null,
  thumbnails: [],
  locations: [],
  flatLocations: {},
  formatedLocations: {},
  selectedLocations: [],
  selectedCounties: [],
  flatCategories: [],
  categories: [],
  selectedCategories: [],
  entityType: null,
  bids: {
    cpc: '',
    daily: '',
  },
  bidsRecommended: null,
  locationPrice: null,
  citySuggestions: {
    loading: true,
    cities: [],
  },
  checkUrlResult: null,
  // Url Input Steps
  urlStep: {
    url: '',
    isLoading: false,
    response: null,
  },
  dailyBudgedModalHidden: false,
  text: {
    url: '',
    displayUrl: '',
    headline: '',
    description: '',
  },
  // Image Promo Payload
  image: {
    // Автопродление включено по умолчанию
    autoRenew: true,
    images: [],
    url: '',
    mainForm: {
      url: '',
      displayUrl: '',
      headline: '',
      description: '',
    },
  },
  // Url Promo Payload
  url: {
    type: null,
    mainForm: {
      url: '',
      onlyfans: '',
    },
  },
  // Promotion Edit
  promotion: null,
};

const formatLocations = (cities: TFlatLocationsMap) => {
  const result: any = {};

  Object.keys(cities).forEach((id) => {
    const location = cities[parseInt(id, 10)];
    if (location.type !== 'city') {
      return;
    }
    const {stateId, countryId, countyId} = location.meta;

    if (countryId) {
      if (result[countryId]) {
        result[countryId].push(id);
      } else {
        result[countryId] = [id];
      }
    }

    if (stateId) {
      if (result[stateId]) {
        result[stateId].push(id);
      } else {
        result[stateId] = [id];
      }
    }

    if (countyId) {
      if (result[countyId]) {
        result[countyId].push(id);
      } else {
        result[countyId] = [id];
      }
    }
  });

  return result;
};

// limit photos in profile
enum photoLimit {
  mobile = 500,
  desktop = 1000,
}

export const promotions = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setLoading: (state: PromotionsState, loading: boolean) => ({
      ...state,
      loading,
    }),
    setPayload: (state: PromotionsState, payload: Partial<PromotionsState>) => ({
      ...state,
      ...payload,
    }),
    setImagePromotionPayload: (state: PromotionsState, payload: any) => ({
      ...state,
      image: {
        ...state.image,
        ...payload,
      },
    }),
    setUrlPromotionPayload: (state: PromotionsState, payload: any) => ({
      ...state,
      url: {
        ...state.url,
        ...payload,
      },
    }),
  },
  effects: (dispatch) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const {promotions} = dispatch;

    return {
      async requestPromotionById(id: number): Promise<void> {
        promotions.setLoading(true);

        try {
          const response = await fetchPromotionById(id);

          promotions.setPayload({
            promotion: response.data,
          });
        } finally {
          promotions.setLoading(false);
        }
      },
      clearPromotion() {
        promotions.setPayload({
          promotion: null,
        });
      },
      async requestLocations(): Promise<void> {
        promotions.setLoading(true);

        try {
          const response = await fetchLocationsList();
          const flatLocs = flatLocations(response.data);

          promotions.setPayload({
            locations: response.data,
            flatLocations: flatLocs,
            formatedLocations: formatLocations(flatLocs),
          });
        } catch (err) {
          promotions.setPayload({isRequestError: true});
        } finally {
          promotions.setLoading(false);
        }
      },
      async requestLocationPrices() {
        try {
          const response = await fetchLoactionPrices();

          promotions.setPayload({
            locationPrice: response.data,
          });
        } catch (err) {
          promotions.setPayload({isRequestError: true});
        }
      },
      async requestCategories(params: FetchPromotionCategoriesParams) {
        try {
          const response = await fetchPromotionCategories(params);
          const flattedCategories = flatCategoires(response.data.categories);

          promotions.setPayload({
            categories: response.data.categories,
            flatCategories: flatCategoires(response.data.categories),
          });

          return {
            categories: response.data.categories,
            flattedCategories,
          };
        } catch (e: any) {
          promotions.setPayload({
            isRequestError: true,
          });
          console.error(e);

          return {
            categories: [],
            flattedCategories: [],
          };
        }
      },
      async requestProfileByContacts({phone, onlyfans, url}): Promise<ProfilesResponse> {
        promotions.setLoading(true);
        const limit = isMobileDevice() ? photoLimit.mobile : photoLimit.desktop;

        try {
          const response = await getProfileByContacts({
            phone,
            onlyfans,
            profile_url: url,
            limit,
            ad: 'image',
          });

          promotions.setPayload({
            profilesResponse: response.data,
            type: phone ? 'phone' : 'onlyfans',
          });

          return response.data;
        } finally {
          promotions.setLoading(false);
        }
      },
      async requestProfileByUrl(url, state): Promise<any> {
        const limit = isMobileDevice() ? photoLimit.mobile : photoLimit.desktop;

        promotions.setPayload({
          urlStep: {
            url,
            isLoading: true,
          },
        });

        // Request data
        const response = await getProfileByContacts({
          phone: state.phone,
          profile_url: url,
          limit,
          ad: 'image',
        });

        promotions.setPayload({
          urlStep: {
            url,
            isLoading: false,
            response: response.data,
          },
        });

        return response.data;
      },
      async checkIsUnique(url): Promise<void> {
        try {
          const response = await checkUniqueness(url);

          return response.data;
        } catch (e) {
          console.error(e);
        }
      },
      async checkUrl(url): Promise<any> {
        try {
          const response = await checkUrl(url);

          return response.data.status;
        } catch (error) {
          if (error?.response?.status >= 500) {
            return 'SYSTEM';
          }

          return error?.response?.data?.status || 'OTHER';
        }
      },
      async requestUrlInfo(url: string): Promise<UrlInfoResponse> {
        try {
          const urlInfoResponse = await getUrlInfo(url);

          return urlInfoResponse.data;
        } catch (error) {
          return error?.response?.data || {};
        }
      },
      async createPromotion(data): Promise<any> {
        const response = await createPromotion(data);

        promotions.setPayload(defaultState);

        return response.data;
      },
      async fetchRecommendedBids(body: FetchBidsParams) {
        promotions.setPayload({
          loading: true,
        });
        const response = await fetchRecommendedBids(body);
        const recommendedCpc = response.data.bid_recommended_price;

        promotions.setPayload({
          bidsRecommended: recommendedCpc,
          loading: false,
        });
      },
      loadCitySuggestions(query: string) {
        const controller = new AbortController();
        promotions.setPayload({
          citySuggestions: {
            loading: true,
            cities: [],
          },
        });
        fetchCitySuggestions(query, controller)
          .then(({data}) => {
            promotions.setPayload({
              citySuggestions: {
                loading: false,
                cities: data.results,
              },
            });
          })
          .catch(() => {
            promotions.setPayload({
              citySuggestions: {
                loading: false,
                cities: [],
              },
            });
          });

        return controller;
      },
      async createImagePromotion(_params: any, state) {
        const promotionsState = state.promotions;
        const profileRoleId = state.profile.data?.roleId;

        if (!profileRoleId) {
          console.error('no profile id data');
          return;
        }
        const body: any = {
          promo: PromotionTextTypes.Image,
          category_ids: promotionsState.selectedCategories,
          details: {
            type: profileRoleId,
            url: promotionsState.image.mainForm.url,
            display_url: promotionsState.image.mainForm.displayUrl,
            images: promotionsState.image.images,
            title: promotionsState.image.mainForm.headline,
            description: promotionsState.image.mainForm.description,
          },
        };

        switch (profileRoleId) {
          case UserRoles.Subscription: {
            body.geo = {
              city_ids: promotionsState.selectedLocations,
              county_ids: promotionsState.selectedCounties,
              state_ids: [],
              country_ids: [],
            };
            body.details.auto_renew = promotionsState.image.autoRenew;
            break;
          }
          case UserRoles.PPC:
            {
              body.locations = promotionsState.selectedLocations;
              body.bids = {
                price: parseFloat(promotionsState.bids?.cpc),
                daily_limit: parseFloat(promotionsState.bids?.daily),
              };
            }
            break;
        }

        console.log(body);
        const {data} = await createCommonPromotion(body);
        const status =
          data.payment_status === 'paid' && data.status === 'active' ? 'active' : 'pending';

        if (data.rest_balance > 0) {
          window.location.href = `/profile/credit?amount=${data.rest_balance}`;
        } else if (status === 'pending') {
          window.location.href = `/profile/image-promotions?status=pending`;
        } else {
          window.location.href = `/profile/image-promotions`;
        }
      },
    };
  },
});
